import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Lock,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import PermissionGuard from '../components/PermissionGuard';
import axios from 'axios';

const ATFCompliance = () => {
  const { currentTenant } = useTenant();
  const [complianceData, setComplianceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const loadComplianceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use currentTenant.id if available, otherwise use a default
      const organizationId = currentTenant?.id || 'demo-organization';
      
      const response = await axios.get('/atf-compliance/validate', {
        params: { organization_id: organizationId }
      });
      
      setComplianceData(response.data);
    } catch (error) {
      console.error('Error loading ATF compliance data:', error);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => {
    // Load data regardless of tenant status
    loadComplianceData();
  }, [loadComplianceData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ATF_COMPLIANT':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Shield className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ATF_COMPLIANT':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Error Loading Data</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={loadComplianceData}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">ATF Compliance Dashboard</h1>
          <p className="text-gray-400 mt-1">
            Federal firearms compliance monitoring for {currentTenant?.custom_branding?.company_name || currentTenant?.name || 'Dark Matter Solutions'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {showDetails ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showDetails ? 'Hide Details' : 'Show Details'}</span>
          </button>
          <button
            onClick={loadComplianceData}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Overall Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-lg p-6 border border-gray-700"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Overall Compliance Status</h2>
          <div className="flex items-center space-x-2">
            {getStatusIcon(complianceData?.compliance_status)}
            <span className={`font-medium ${getStatusColor(complianceData?.compliance_status)}`}>
              {complianceData?.compliance_status || 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-400" />
              <div>
                <div className="text-sm text-gray-400">Registry Status</div>
                <div className="text-lg font-semibold text-white">
                  {complianceData?.registry_violation ? 'Violation' : 'Compliant'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Lock className="w-8 h-8 text-green-400" />
              <div>
                <div className="text-sm text-gray-400">Local Control</div>
                <div className="text-lg font-semibold text-white">
                  {complianceData?.local_control_verified ? 'Verified' : 'Not Verified'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-purple-400" />
              <div>
                <div className="text-sm text-gray-400">Cross-Sharing</div>
                <div className="text-lg font-semibold text-white">
                  {complianceData?.cross_sharing_prevented ? 'Prevented' : 'Not Prevented'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Compliance Checks */}
      {showDetails && complianceData?.compliance_checks && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-lg p-6 border border-gray-700"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Detailed Compliance Checks</h2>
          
          <div className="space-y-4">
            {Object.entries(complianceData.compliance_checks).map(([checkName, checkData]) => (
              <div key={checkName} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-white capitalize">
                    {checkName.replace(/_/g, ' ')}
                  </h3>
                  <div className="flex items-center space-x-2">
                    {checkData.atf_compliant ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={`text-sm font-medium ${
                      checkData.atf_compliant ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {checkData.atf_compliant ? 'Compliant' : 'Non-Compliant'}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(checkData).map(([key, value]) => {
                    if (key === 'validation_timestamp' || key === 'atf_compliant') return null;
                    
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-gray-400 capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className={`text-sm font-medium ${
                          typeof value === 'boolean' 
                            ? (value ? 'text-green-400' : 'text-red-400')
                            : 'text-white'
                        }`}>
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Action Items */}
      <PermissionGuard requiredPermission="write">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800 rounded-lg p-6 border border-gray-700"
        >
          <h2 className="text-xl font-semibold text-white mb-4">Recommended Actions</h2>
          
          <div className="space-y-3">
            {complianceData?.registry_violation && (
              <div className="flex items-center space-x-3 p-3 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-red-200">
                  Review data sharing practices to ensure no national registry is created
                </span>
              </div>
            )}
            
            {!complianceData?.local_control_verified && (
              <div className="flex items-center space-x-3 p-3 bg-yellow-900 bg-opacity-20 border border-yellow-700 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-200">
                  Verify local data control measures are properly implemented
                </span>
              </div>
            )}
            
            {complianceData?.compliance_status === 'ATF_COMPLIANT' && (
              <div className="flex items-center space-x-3 p-3 bg-green-900 bg-opacity-20 border border-green-700 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-200">
                  All compliance requirements are currently met. Continue monitoring.
                </span>
              </div>
            )}
          </div>
        </motion.div>
      </PermissionGuard>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        <p>Last updated: {complianceData?.report_timestamp ? new Date(complianceData.report_timestamp).toLocaleString() : 'Unknown'}</p>
        <p className="mt-1">ATF Compliance ID: {currentTenant?.id || 'demo-organization'}</p>
      </div>
    </div>
  );
};

export default ATFCompliance;
